import { useCallback, useEffect, useRef } from 'react';
import { useDebounce } from 'Helper/debounce';

import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';

import { MenuBar } from 'UiKit/rich-text/menu-bar';
import { ImageExtension } from 'UiKit/rich-text/image-extension';

import styles from './styles.module.css';

const RichTextEditor = ({ value = '', onChange, onBlur, error, errorMessage, placeholder = 'Type something...' }) => {
  const isSyncingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const debouncedOnChange = useDebounce(
    useCallback((html) => onChangeRef.current?.(html), []),
    150,
  );

  const editor = useEditor({
    editorProps: {
      attributes: { spellcheck: 'false', class: styles['tiptap'] },
    },
    extensions: [
      StarterKit.configure({ link: false, underline: false }),
      TextStyle,
      FontSize,
      Underline,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['paragraph', 'heading'] }),
      ImageExtension,
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    onUpdate({ editor }) {
      if (isSyncingRef.current) return;
      debouncedOnChange(editor.getHTML());
    },
    onBlur() {
      onBlur?.();
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      isSyncingRef.current = true;
      editor.commands.setContent(value, false);
      isSyncingRef.current = false;
    }
  }, [value, editor]);

  return (
    <div className={`${styles['editor-wrapper']} ${error ? styles['editor-wrapper--error'] : ''}`}>
      <MenuBar editor={editor} />
      <EditorContent className={styles['editor-content-area']} editor={editor} />
      {error && errorMessage && <span className={styles['editor-error-message']}>{errorMessage}</span>}
    </div>
  );
};

export default RichTextEditor;
